/*
 * @providesModule AuthenticationController
 */
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import FTUEController from 'FTUEController';
import ModalNavigationController from 'ModalNavigationController';
import LeagueFTUEController from 'LeagueFTUEController';
import LoadingIndicator from 'LoadingIndicator';
import MatchJoinController from 'MatchJoinController';
import {Navigator} from 'react-native-deprecated-custom-components';
import NavigationManager from 'NavigationManager';
import NetClient from 'NetClient';
import {ErrorResponse} from 'NetClientTypes';
import Prefs from 'Preferences';
import SKZAPIConstants from 'SKZAPIConstants';
import SMSVerifyController from 'SMSVerifyController';
import {ServerErrorResponse} from 'SharedTypes';
import _ from 'lodash';
import React from 'react';
import {I18nextProvider} from 'react-i18next';
import i18nextInstance from 'i18nextInstance';
import {NativeModules, Platform} from 'react-native';
import SignInView, {TabType} from 'SignInView';
import Tournament from 'SKZTournament';
import {SKZUserInterface} from 'SKZUser';
import {
  FORGOT_PASSWORD_ERROR,
  FORGOT_PASSWORD_SUBMIT_CLICK,
  SIGN_IN_APPLE_CLICK,
  SIGN_IN_APPLE_ERROR,
  SIGN_IN_EMAIL_CLICK,
  SIGN_IN_FACEBOOK_CLICK,
  SIGN_IN_FACEBOOK_ERROR,
  SIGN_IN_GOOGLE_CLICK,
  SIGN_IN_GOOGLE_ERROR,
  SIGN_IN_USER_SUCCESS,
  SIGN_UP_APPLE_CLICK,
  SIGN_UP_APPLE_ERROR,
  SIGN_UP_APPLE_EXISTING_ACCOUNT,
  SIGN_UP_EMAIL_CLICK,
  SIGN_UP_EMAIL_ERROR,
  SIGN_UP_EMAIL_EXISTING_ACCOUNT,
  SIGN_UP_FACEBOOK_CLICK,
  SIGN_UP_FACEBOOK_ERROR,
  SIGN_UP_FACEBOOK_EXISTING_ACCOUNT,
  SIGN_UP_GOOGLE_CLICK,
  SIGN_UP_GOOGLE_ERROR,
  SIGN_UP_GOOGLE_EXISTING_ACCOUNT,
} from 'AWSConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import MergeAccountController from 'MergeAccountController';
import SkillzRouter from 'SkillzRouter';
import SkillzStyle from 'SkillzStyle';
import {verifyEmailOTP} from 'EmailOTPUtils';
import {useUnifiedLoginEvents} from 'UnifiedLoginEvents';
const SkillzAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;
const GAME_ID = SkillzModule.App.GameId;
const SKZReactSaveAccountManager = NativeModules.SKZReactSaveAccountManager;
const REQUIRE_ACCOUNT_SAVE_KEY = 'needsDepositInfoSKZ';
const ON_SAVE_ACCOUNT_SCREEN = 'onSaveAccountScreenSKZ';
const ReactVCManager = NativeModules.SKZReactViewControllerManager;
const ReactUserManager = NativeModules.SKZReactUserManager;
const ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT = 409;
let hasNotifiedReady = false;

type IDPToken = {
  expires_in: number;
  refresh_token: string;
  id_token: string;
  access_token: string;
};
export const authenticateUserWithIDP = (fedType: string): Promise<boolean> => {
  return new Promise((resolve: (response: boolean) => void) => {
    AuthenticationController.getOpenAuthFederatedToken(
      fedType,
      (tokenInfo: IDPToken | null, error: any) => {
        if (!error && tokenInfo) {
          SkillzModule.authenticateThirdPartyUserReact(
            fedType,
            tokenInfo.id_token,
            (success: boolean) => {
              resolve(success);
            }
          );
        } else {
          resolve(false);
        }
      }
    );
  });
};
export type FTUECredentials = {
  authorization: string; // JWT for FTUE Account will be used for merging FTUE account after login
  skillzUser: SKZUserInterface; //user for FTUE Account will be used for merging FTUE account after login
};
interface LoginWithSMSRedirectProps {
  phoneNumber?: string;
  country?: string;
  countryValue?: string;
  userAlreadyHaveAccount?: boolean;
}

interface LoginWithEmailRedirectProps {
  email?: string;
}

const AuthenticationController = {
  async getFtueCredentials(): Promise<FTUECredentials | null> {
    return new Promise(
      (
        resolve: (ftueCredentials: FTUECredentials | null) => void,
        _reject: (error: Error) => void
      ) => {
        AuthenticationController.forceRefresh().finally(() => {
          NativeModules.SKZAPIManager.fetchAuthCredential((token: string) => {
            CurrentUserStore.fetchUser()
              .then((userInfo: SKZUserInterface) => {
                resolve({skillzUser: userInfo, authorization: token});
              })
              .catch((error: Error) => {
                console.error(
                  'getFtueCredentials: Cannot fetch user info:' + JSON.stringify(error)
                );
                resolve(null);
              });
          });
        });
      }
    );
  },

  async forceRefresh(): Promise<void> {
    return new Promise<void>((resolve: () => void) => {
      if (NativeModules.SKZAPIManager?.refreshAuthToken) {
        NativeModules.SKZAPIManager.refreshAuthToken(() => resolve());
      } else {
        resolve();
      }
    });
  },

  getIdentityBaseURL(): string {
    let env = '';
    switch (SkillzAPIManager.CurrentEnvironment) {
      case SkillzAPIManager.Environment.Prod:
        env = SKZAPIConstants.SKZ_API_BASE_PROD;
        break;
      case SkillzAPIManager.Environment.QA:
        env = SKZAPIConstants.SKZ_API_BASE_QA;
        break;
      case SkillzAPIManager.Environment.Sandbox:
        env = SKZAPIConstants.SKZ_API_BASE_SAND;
        break;
      case SkillzAPIManager.Environment.Staging:
        env = SKZAPIConstants.SKZ_API_BASE_STAGE;
        break;
      case SkillzAPIManager.Environment.ProxyTS:
        env = SKZAPIConstants.SKZ_API_BASE_PROXY_TS;
        break;
      default:
        env = SKZAPIConstants.SKZ_API_BASE_QA;
        break;
    }
    const baseURL = 'https://identity.' + env + 'sign-in/sdk/';
    return baseURL;
  },

  getOpenAuthFederatedToken(
    fedType: string,
    completion: (tokenInfo: IDPToken | null, error: ServerErrorResponse | null) => void
  ) {
    const url = AuthenticationController.getIdentityBaseURL() + fedType;
    const bundleId = _.get(SkillzModule, 'App.PackageId', '');
    const redirectUrl = 'skillz.auth.' + bundleId;
    const params = {
      client_id: GAME_ID,
      platform: Platform.OS.toUpperCase(),
      bundle_id: bundleId,
      scope: 'sdk',
    };
    const ReactAuthModule =
      Platform.OS == 'ios'
        ? NativeModules.SKZReactViewControllerManager
        : NativeModules.ReactViewAuthenticationModule;
    ReactAuthModule.openAuth(url, redirectUrl, params)
      .then((result: any) => {
        if (result != null) {
          if (result.type == 'success') {
            LoadingIndicator.show();
            this.authorizeUserWithFederatedToken(result.code, fedType, null)
              .then((tokenInfo: IDPToken) => {
                completion(tokenInfo, null);
              })
              .catch((error: ServerErrorResponse) => {
                LoadingIndicator.hide();
                switch (fedType.toUpperCase()) {
                  case 'GOOGLE':
                    AWSManager.trackEvent(SIGN_IN_GOOGLE_ERROR);
                    break;
                  case 'APPLE':
                    AWSManager.trackEvent(SIGN_IN_APPLE_ERROR);
                    break;
                  case 'FACEBOOK':
                    AWSManager.trackEvent(SIGN_IN_FACEBOOK_ERROR);
                    break;
                }
                completion(null, error);
              });
          }
        }
      })
      .catch(() => {
        console.warn('User Cancelled');
      });
  },

  async loginUserWithIDP(fedType: string): Promise<boolean> {
    switch (fedType.toUpperCase()) {
      case 'GOOGLE':
        AWSManager.trackEvent(SIGN_IN_GOOGLE_CLICK);
        break;
      case 'APPLE':
        AWSManager.trackEvent(SIGN_IN_APPLE_CLICK);
        break;
      case 'FACEBOOK':
        AWSManager.trackEvent(SIGN_IN_FACEBOOK_CLICK);
        break;
    }
    return new Promise((resolve: (response: boolean) => void) => {
      AuthenticationController.getOpenAuthFederatedToken(
        fedType,
        (tokenInfo: IDPToken | null, error: any) => {
          if (!error && tokenInfo) {
            SkillzModule.loginUserWithThirdParty(
              fedType,
              tokenInfo.id_token,
              tokenInfo.refresh_token,
              true,
              (_success: boolean) => {
                LoadingIndicator.hide();
              }
            );
          } else {
            resolve(false);
          }
        }
      );
    });
  },

  /**
   * 
    SignIn : check
    SignUp(Save Account): 
      1. Existing account with Apple auth should also trigger Merge Account flow- check
      2. New account with apple auth - check
    Merge Account within  save account flow

   */

  _onContinuePressWithIDP({
    autoMerge,
    fedType,
    onDone,
    tournament,
    navigator,
  }: {
    autoMerge: boolean | undefined;
    fedType: string;
    onDone?: (success: boolean) => void;
    tournament: Tournament;
    navigator: any;
  }) {
    LoadingIndicator.show();
    const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
    const shouldAutoMerge = AuthenticationController.handleAutoMerge(
      autoMerge,
      hasSeenFTUETutorial
    );
    AuthenticationController.getFtueCredentials().then((data: FTUECredentials | null) => {
      if (Platform.OS === 'ios') {
        // Turning off js loading indicator at this point helps, to prevent the overlap with native loading indicator.
        // Timeout will make the js loader stay little bit longer, until the native indicator renders, -> smoother UX.
        setTimeout(() => LoadingIndicator.hide(), 500);
      }
      AuthenticationController.signUpUserWithIDP(
        fedType,
        (success) => {
          if (onDone) onDone(success);
          if (success) {
            if (ClientPropertyConstants.getSMSSaveAccount()) {
              if (Platform.OS !== 'ios') {
                LoadingIndicator.show();
              }

              MergeAccountController.createMergeAccountLink(data)
                .then(() => MergeAccountController.startMergeAccountsFlow(data, shouldAutoMerge))
                .catch((error: ErrorResponse) => {
                  MergeAccountController.startAccountMergeErrorSteps(shouldAutoMerge);
                  console.error('Merge Account Link Error: ' + JSON.stringify(error));
                });
            } else if (Platform.OS === 'web') {
              LoadingIndicator.hide();
              ModalNavigationController.hideModal(() => SkillzRouter.backToRoot());
            }
          }
        },
        tournament,
        navigator
      );
    });
  },

  notifySDKHasLoaded(isScreenUp: boolean) {
    Prefs.set({[ON_SAVE_ACCOUNT_SCREEN]: isScreenUp});
    if (!hasNotifiedReady) {
      SkillzModule.reactSDKHasLoaded?.();
      hasNotifiedReady = true;
    }
  },

  authorizeUserWithFederatedToken(
    accessCode: string,
    fedType: string,
    state: string | null
  ): Promise<IDPToken> {
    const path = `/identity/v1/federations/authorize`;
    const params = {
      authorization_code: accessCode,
      state: state,
      auth_type: fedType,
    };

    return new Promise(
      (resolve: (tokenInfo: IDPToken) => void, reject: (error: ErrorResponse) => void) => {
        NetClient.postURL(
          path,
          params,
          SkillzAPIManager.OnError.failSilently,
          (error: ErrorResponse | null, payload: unknown | undefined) => {
            if (error) {
              console.debug(
                'Failed to fetch federated tokens from : ' + fedType + 'with error :' + error
              );
              reject(error);
            } else {
              resolve(payload as IDPToken);
            }
          }
        );
      }
    );
  },

  transitionToNextScreen(
    onDone?: (success: boolean) => void,
    tournament?: Tournament,
    navigator?: Navigator
  ) {
    Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});
    if (onDone) {
      onDone(true);
      return;
    }
    if (tournament) {
      MatchJoinController.continueJoinValidationForTournament(tournament, navigator as Navigator);
    } else {
      SKZReactSaveAccountManager.executeStoredCompletion((didExecuteCompletion: boolean) => {
        if (!didExecuteCompletion) {
          ReactVCManager.popToRoot(true, () => {
            /* */
          });
        }
      });
    }
  },

  async saveUserWithUsername(
    email: string,
    password: string,
    onDone?: (success: boolean) => void,
    tournament?: Tournament,
    navigator?: Navigator
  ): Promise<string | null> {
    LoadingIndicator.show();
    AWSManager.trackEvent(SIGN_UP_EMAIL_CLICK);
    return new Promise((resolve: (response: string | null) => void) => {
      ReactUserManager.getUserPass((error: any, pass: string | null) => {
        if (error) {
          console.warn('Error getting user password: ' + error);
          resolve('ERROR');
        } else {
          CurrentUserStore.updateUser({
            email: email,
            password: password,
            old_password: pass,
          })
            .then(() => {
              Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});
              LoadingIndicator.hide();
              SkillzModule.updateNativeUser();
              if (Platform.OS == 'android' && tournament?.live_event) {
                LoadingIndicator.show();
                console.debug('SaveAccount: adding timeout to android live event transition');
                setTimeout(() => {
                  LoadingIndicator.hide();
                  AuthenticationController.transitionToNextScreen(onDone, tournament, navigator);
                }, 3500); // eslint-disable-line no-magic-numbers
              } else {
                AuthenticationController.transitionToNextScreen(onDone, tournament, navigator);
              }
              resolve(null);
            })
            .catch((alertError: any) => {
              LoadingIndicator.hide();
              if (alertError.includes('already using this email')) {
                AWSManager.trackEvent(SIGN_UP_EMAIL_EXISTING_ACCOUNT);
                resolve('SIGN_UP_EMAIL_EXISTING_ACCOUNT');
              } else {
                AWSManager.trackEvent(SIGN_UP_EMAIL_ERROR);
                resolve('ERROR');
              }
            });
        }
      });
    });
  },

  saveUserWithFederatedToken(accessCode: string, fedType: string): Promise<ServerErrorResponse> {
    return CurrentUserStore.fetchUser(true).then((user: {id: string}) => {
      const url = '/identity/v1/user-identities';
      const params = {
        id_token: accessCode,
        user_id: user.id,
        auth_type: fedType.toUpperCase(),
      };
      return new Promise((resolve: (error: ErrorResponse | null) => void) => {
        NetClient.postURL(
          url,
          params,
          SkillzAPIManager.OnError.failSilently,
          (error: ErrorResponse | null, _payload: unknown | undefined) => {
            if (!error) {
              resolve(null);
            } else {
              if (error.code === ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT) {
                switch (fedType.toUpperCase()) {
                  case 'GOOGLE':
                    AWSManager.trackEvent(SIGN_UP_GOOGLE_EXISTING_ACCOUNT);
                    break;
                  case 'APPLE':
                    AWSManager.trackEvent(SIGN_UP_APPLE_EXISTING_ACCOUNT);
                    break;
                  case 'FACEBOOK':
                    AWSManager.trackEvent(SIGN_UP_FACEBOOK_EXISTING_ACCOUNT);
                    break;
                }
              } else {
                switch (fedType.toUpperCase()) {
                  case 'GOOGLE':
                    AWSManager.trackEvent(SIGN_UP_GOOGLE_ERROR);
                    break;
                  case 'APPLE':
                    AWSManager.trackEvent(SIGN_UP_APPLE_ERROR);
                    break;
                  case 'FACEBOOK':
                    AWSManager.trackEvent(SIGN_UP_FACEBOOK_ERROR);
                    break;
                }
              }
              resolve(error);
            }
          }
        );
      });
    });
  },

  async signUpUserWithIDP(
    fedType: string,
    onDone?: (success: boolean) => void,
    tournament?: Tournament,
    navigator?: Navigator
  ): Promise<number> {
    switch (fedType.toUpperCase()) {
      case 'GOOGLE':
        AWSManager.trackEvent(SIGN_UP_GOOGLE_CLICK);
        break;
      case 'APPLE':
        AWSManager.trackEvent(SIGN_UP_APPLE_CLICK);
        break;
      case 'FACEBOOK':
        AWSManager.trackEvent(SIGN_UP_FACEBOOK_CLICK);
        break;
    }
    return new Promise((resolve: (response: number) => void) => {
      AuthenticationController.getOpenAuthFederatedToken(
        fedType,
        (tokenInfo: IDPToken | null, error: ServerErrorResponse | null) => {
          if (!error && tokenInfo) {
            AuthenticationController.saveUserWithFederatedToken(tokenInfo.id_token, fedType).then(
              (fedError: ServerErrorResponse) => {
                if (fedError?.code === 409) {
                  SkillzModule.loginUserWithThirdParty(
                    fedType,
                    tokenInfo.id_token,
                    tokenInfo.refresh_token,
                    true,
                    (_success: boolean) => {
                      if (onDone) onDone(_success);
                      setTimeout(() => {
                        LoadingIndicator.hide();
                      }, 1000);
                    }
                  );
                  return;
                }

                if (!fedError) {
                  SkillzModule.saveUserAccount(
                    fedType,
                    tokenInfo.id_token,
                    (_saveUserError: any, success: boolean) => {
                      LoadingIndicator.hide();
                      if (success) {
                        Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});
                        if (Platform.OS == 'android' && tournament?.live_event) {
                          LoadingIndicator.show();
                          setTimeout(() => {
                            LoadingIndicator.hide();
                            AuthenticationController.transitionToNextScreen(
                              onDone,
                              tournament,
                              navigator
                            );
                          }, 3500); // eslint-disable-line no-magic-numbers
                        } else {
                          AuthenticationController.transitionToNextScreen(
                            onDone,
                            tournament,
                            navigator
                          );
                        }
                      }
                    }
                  );
                } else {
                  LoadingIndicator.hide();
                  resolve(fedError.code);
                }
              }
            );
          } else {
            if (error) {
              resolve(error?.code);
            }
          }
        }
      );
    });
  },

  async forgotPasswordAction(username: string): Promise<boolean> {
    AWSManager.trackEvent(FORGOT_PASSWORD_SUBMIT_CLICK);
    return new Promise((resolve: (response: boolean) => void) => {
      SkillzModule.sendForgotPassword(username, (success: boolean) => {
        if (!success) {
          AWSManager.trackEvent(FORGOT_PASSWORD_ERROR);
        }
        resolve(success);
      });
    });
  },

  /**
   * Returns the same sign-in handler used by SignInView: (username, password) => void.
   * Use this in UnifiedLogin/EmailRegister to share the exact same flow.
   */
  getOnSignInPress(shouldArchiveUser?: boolean) {
    return (username: string, password: string) => {
      AuthenticationController.onSignInWithUserArchiveCallback(
        username,
        password,
        shouldArchiveUser,
        true
      );
    };
  },

  onSignInWithUserArchiveCallback(
    username: string,
    password: string,
    shouldArchiveUser?: boolean,
    autoMerge?: boolean
  ) {
    const {trackSignInEmailPageClicked} = useUnifiedLoginEvents();
    LoadingIndicator.show();
    AWSManager.trackEvent(SIGN_IN_EMAIL_CLICK);
    const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
    const shouldAutoMerge = AuthenticationController.handleAutoMerge(
      autoMerge,
      hasSeenFTUETutorial
    );
    AuthenticationController.getFtueCredentials().then((data: FTUECredentials | null) => {
      if (Platform.OS === 'ios') {
        // Turning off js loading indicator at this point helps, to prevent the overlap with native loading indicator.
        // Timeout will make the js loader stay little bit longer, until the native indicator renders, -> smoother UX.
        setTimeout(() => LoadingIndicator.hide(), 500);
      }
      SkillzModule.loginUserWithUserName(
        username,
        password,
        shouldArchiveUser ?? false,
        (success: boolean) => {
          LoadingIndicator.hide();
          if (!success) {
            const signInMode = username?.includes('@') ? 'email' : 'phone';
            if (ClientPropertyConstants.isFTUEv3Enabled()) {
              trackSignInEmailPageClicked('Next', 'Sign_in_page', false);
            }
            trackTelemetryEvent({
              event: TelemetryEventName.SIGN_IN_SUCCESS,
              payload: {
                is_guest_account: !CurrentUserStore?.isSavedAccount(),
                sign_in_status: false,
                sign_in_mode: signInMode,
              },
            });
            return;
          }

          CurrentUserStore.fetchUser().then((primaryUser: SKZUserInterface) => {
            AWSManager.trackEvent(SIGN_IN_USER_SUCCESS, {data: primaryUser.id});
            const signInMode = username?.includes('@') ? 'email' : 'phone';
            if (ClientPropertyConstants.isFTUEv3Enabled()) {
              trackSignInEmailPageClicked('Next', 'Sign_in_page', true);
            }
            trackTelemetryEvent({
              event: TelemetryEventName.SIGN_IN_SUCCESS,
              payload: {
                is_guest_account: !CurrentUserStore?.isSavedAccount(),
                sign_in_status: true,
                sign_in_mode: signInMode,
              },
            });
          });
          // FTUE prefs should be marked as true on logging in so returning users don't see FTUE
          LeagueFTUEController.setHasSeenLeagueFTUE(true);
          Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});

          if (ClientPropertyConstants.getSMSSaveAccount()) {
            if (Platform.OS !== 'ios') {
              LoadingIndicator.show();
            }
            MergeAccountController.createMergeAccountLink(data)
              .then(() => MergeAccountController.startMergeAccountsFlow(data, shouldAutoMerge))
              .catch((error: ErrorResponse) => {
                MergeAccountController.startAccountMergeErrorSteps(shouldAutoMerge);
                console.error('Merge Account Link Error: ' + JSON.stringify(error));
              });
          } else if (Platform.OS === 'web') {
            LoadingIndicator.hide();
            ModalNavigationController.hideModal(() => SkillzRouter.backToRoot());
          }
        }
      );
    });
  },

  handleAutoMerge(autoMerge: boolean | undefined, hasSeenFTUETutorial: boolean) {
    return ClientPropertyConstants.isFTUEv3Enabled() ? true : autoMerge ?? !hasSeenFTUETutorial;
  },

  onSignInWithEmailOTPCallback(
    email: string,
    token: string,
    isSignUp: boolean,
    provide_email_verification_bonus: boolean,
    onDone: ({data, success}: {data: any; success: boolean}) => void,
    autoMerge?: boolean
  ) {
    LoadingIndicator.show();
    AWSManager.trackEvent(SIGN_IN_EMAIL_CLICK);
    const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
    const shouldAutoMerge = AuthenticationController.handleAutoMerge(
      autoMerge,
      hasSeenFTUETutorial
    );

    // Native platforms (Android/iOS) - Use native login for both sign-up and sign-in
    if (Platform.OS !== 'web') {
      // Get FTUE credentials first for merge account flow
      AuthenticationController.getFtueCredentials().then((ftueData: FTUECredentials | null) => {
        if (isSignUp) {
          // SIGN-UP Flow: Only verify email (user is already logged in as guest user)
          // Email verification just adds/verifies email to their existing guest account
          verifyEmailOTP(
            {email, token, isSignUp: true, provide_email_verification_bonus},
            (data: any, emailVerified: boolean) => {
              LoadingIndicator.hide();

              if (!emailVerified) {
                onDone({data, success: false});
                return;
              }

              // Email verified successfully - user data is already saved (they're the guest user)
              CurrentUserStore.fetchUser().then((primaryUser: SKZUserInterface) => {
                AWSManager.trackEvent(SIGN_IN_USER_SUCCESS, {data: primaryUser.id});
                const signInMode = 'email';
                trackTelemetryEvent({
                  event: TelemetryEventName.SIGN_IN_SUCCESS,
                  payload: {
                    is_guest_account: !CurrentUserStore?.isSavedAccount(),
                    sign_in_status: true,
                    sign_in_mode: signInMode,
                  },
                });
              });

              LeagueFTUEController.setHasSeenLeagueFTUE(true);
              Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});

              onDone({data: {}, success: true});
            }
          );
        } else {
          // SIGN-IN Flow: Directly use native OAuth login (calls /oauth/email and saves user data)
          SkillzModule.loginUserWithEmail(
            email,
            token,
            shouldAutoMerge,
            (error: any, success: boolean) => {
              LoadingIndicator.hide();
              if (success) {
                // Native side has handled everything: user data, storage, navigation
                CurrentUserStore.fetchUser().then((primaryUser: SKZUserInterface) => {
                  AWSManager.trackEvent(SIGN_IN_USER_SUCCESS, {data: primaryUser.id});
                  const signInMode = 'email';
                  trackTelemetryEvent({
                    event: TelemetryEventName.SIGN_IN_SUCCESS,
                    payload: {
                      is_guest_account: !CurrentUserStore?.isSavedAccount(),
                      sign_in_status: true,
                      sign_in_mode: signInMode,
                    },
                  });
                });

                LeagueFTUEController.setHasSeenLeagueFTUE(true);
                Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});

                // Trigger merge account flow for sign-in success
                if (ClientPropertyConstants.getSMSSaveAccount()) {
                  LoadingIndicator.show();
                  MergeAccountController.createMergeAccountLink(ftueData)
                    .then(() =>
                      MergeAccountController.startMergeAccountsFlow(ftueData, shouldAutoMerge)
                    )
                    .catch((error: ErrorResponse) => {
                      MergeAccountController.startAccountMergeErrorSteps(shouldAutoMerge);
                      console.error('Merge Account Link Error: ' + JSON.stringify(error));
                    });
                } else {
                  LoadingIndicator.hide();
                  ModalNavigationController.hideModal(() => SkillzRouter.backToRoot());
                }
                onDone({data: {}, success: true});
              } else {
                const errorMessage = error?.responseObject?.localizedDescription || 'Login failed';
                trackTelemetryEvent({
                  event: TelemetryEventName.SIGN_IN_SUCCESS,
                  payload: {
                    is_guest_account: !CurrentUserStore?.isSavedAccount(),
                    sign_in_status: false,
                    sign_in_mode: 'email',
                  },
                });
                onDone({data: {message: errorMessage}, success: false});
              }
            }
          );
        }
      });
    } else {
      // WEB Platform: use JS verifyEmailOTP for both sign-up and sign-in
      AuthenticationController.getFtueCredentials().then((ftueData: FTUECredentials | null) => {
        verifyEmailOTP(
          {email, token, isSignUp, provide_email_verification_bonus},
          (data: any, success: boolean) => {
            LoadingIndicator.hide();
            if (!success) {
              onDone({data, success});
              return;
            }

            CurrentUserStore.fetchUser().then((primaryUser: SKZUserInterface) => {
              AWSManager.trackEvent(SIGN_IN_USER_SUCCESS, {data: primaryUser.id});
              const signInMode = 'email';
              trackTelemetryEvent({
                event: TelemetryEventName.SIGN_IN_SUCCESS,
                payload: {
                  is_guest_account: !CurrentUserStore?.isSavedAccount(),
                  sign_in_status: true,
                  sign_in_mode: signInMode,
                },
              });
            });

            LeagueFTUEController.setHasSeenLeagueFTUE(true);
            Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});

            // Trigger merge account flow for web success
            if (ClientPropertyConstants.getSMSSaveAccount()) {
              LoadingIndicator.show();
              MergeAccountController.createMergeAccountLink(ftueData)
                .then(() =>
                  MergeAccountController.startMergeAccountsFlow(ftueData, shouldAutoMerge)
                )
                .catch((error: ErrorResponse) => {
                  MergeAccountController.startAccountMergeErrorSteps(shouldAutoMerge);
                  console.error('Merge Account Link Error: ' + JSON.stringify(error));
                });
            } else {
              LoadingIndicator.hide();
              ModalNavigationController.hideModal(() => SkillzRouter.backToRoot());
            }
            onDone({data, success});
          }
        );
      });
    }
  },

  onSignInWithEmailOTP(
    email: string,
    token: string,
    isSignUp: boolean,
    onDone: ({data, success}: {data: any; success: boolean}) => void,
    autoMerge: boolean = true
  ) {
    const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
    // const shouldAutoMerge = !hasSeenFTUETutorial && false;
    const shouldAutoMerge = AuthenticationController.handleAutoMerge(
      autoMerge,
      hasSeenFTUETutorial
    );
    AuthenticationController.onSignInWithEmailOTPCallback(
      email,
      token,
      isSignUp,
      false,
      onDone,
      shouldAutoMerge
    );
  },

  displaySignInScreen(
    shouldArchiveUser: boolean,
    loginSMSRedirectProps?: LoginWithSMSRedirectProps,
    loginEmailRedirectProps?: LoginWithEmailRedirectProps,
    autoMerge?: boolean,
    colorVariant?: 'dark',
    initialTab?: TabType,
    showMergePrompt: boolean = false
  ) {
    const codeValidation = (
      code: string,
      phone: string,
      onSuccess: () => void,
      onFailure: (error: string) => void
    ): void => {
      SMSVerifyController.loginWithSMS(
        code,
        phone,
        onSuccess,
        onFailure,
        autoMerge,
        undefined,
        showMergePrompt
      );
    };

    const onSignInPress = (username: string, password: string) => {
      AuthenticationController.onSignInWithUserArchiveCallback(
        username,
        password,
        shouldArchiveUser,
        autoMerge
      );
    };

    const onSignInWithEmailOTP = (
      email: string,
      token: string,
      isSignUp: boolean,
      onDone: ({data, success}: {data: any; success: boolean}) => void
    ) => {
      const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
      const shouldAutoMerge = autoMerge ?? !hasSeenFTUETutorial;
      AuthenticationController.onSignInWithEmailOTPCallback(
        email,
        token,
        isSignUp,
        false,
        onDone,
        shouldAutoMerge
      );
    };

    const props = {
      forgotPasswordAction:
        AuthenticationController.forgotPasswordAction.bind(AuthenticationController),
      onClosePress: () => {
        if (Platform.OS === 'web') {
          ModalNavigationController.hideModal();
        } else {
          NavigationManager.pop();
        }
      },
      onSignInPress,
      email: loginEmailRedirectProps?.email,
      SMSProps: {
        isValidNumber: SMSVerifyController.isValidNumber,
        sendSMSVerification: SMSVerifyController.requestVerificationSignIn,
        fetchCountry: SMSVerifyController.fetchCountry,
        formatNumber: SMSVerifyController.formatNumber,
        getCountryCode: SMSVerifyController.getCountryCode,
        getFlag: SMSVerifyController.getFlag,
        countryData: SMSVerifyController.getCountryData(),
        numberOfInputs: 5,
        codeValidation,
        loading: {show: () => LoadingIndicator.show(), hide: () => LoadingIndicator.hide()},
        inputPhoneNumber: loginSMSRedirectProps?.phoneNumber,
        inputCountry: loginSMSRedirectProps?.country,
        inputCountryValue: loginSMSRedirectProps?.countryValue,
        userAlreadyHaveAccount: loginSMSRedirectProps?.userAlreadyHaveAccount,
        onPhoneNumberInUse: (inputPhoneNumber: string, country: string, countryValue: string) => {
          AuthenticationController.displaySignInScreen(
            false,
            {
              phoneNumber: inputPhoneNumber,
              country: country,
              countryValue: countryValue,
            },
            {},
            autoMerge,
            colorVariant,
            initialTab,
            showMergePrompt
          );
        },
      },
      trackEvent: AWSManager.trackEvent,
      tabClientProps: {
        email: ClientPropertyConstants.getSSOLoginEmailEnabled(),
        phone:
          ClientPropertyConstants.getSSOLoginSMSEnabled() &&
          SkillzModule.loginWithSMSEnabled &&
          !loginEmailRedirectProps,
        emailInitial: ClientPropertyConstants.getSSOLoginEmailDefault(),
      },
      colorVariant,
      initialTab,
      // use for email
      onSignInWithEmailOTP,
    };
    const modal = (
      <I18nextProvider i18n={i18nextInstance}>
        <SignInView
          isNewUI={ClientPropertyConstants.getFTUEV2FeatureEnabled()}
          gameTheme={SkillzStyle.getOverriddenFallbackTheme()}
          onContinueWithIDPPress={(fedtype, onDone, tournament, navigator) =>
            AuthenticationController._onContinuePressWithIDP({
              autoMerge: undefined,
              fedType: fedtype,
              onDone,
              tournament,
              navigator,
            })
          }
          {...props}
        />
      </I18nextProvider>
    );
    if (Platform.OS === 'web') {
      ModalNavigationController.showModal(modal, {
        animationType: 'fade',
        transparent: true,
      });
    } else {
      NavigationManager.push(modal, {scene: 'SignIn'});
    }
  },
};

export {AuthenticationController as default};
export const getOnSignInPress =
  AuthenticationController.getOnSignInPress.bind(AuthenticationController);
